import {
	ActionKind,
	OrderSampleChoice,
	OrderSampleLifecycleStepEnum as SampleStepEnum,
	OrderSampleStatus,
	SampleActionActor,
	type GetClientOrderQuery,
} from '@codezo/graphql';
import { Badge } from '@codezo/quote-detail';
import {
	ActionBox,
	ActionContext,
	SampleRounds,
	type SampleRound,
	type SampleStep,
} from '@codezo/order-detail';
import { Check, ExternalLink, FileText, Video, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { FilePreview, type FilePreviewItem } from '../../../components/ui/file-preview';
import {
	ClientSampleActionModal,
	type ClientSampleAction,
} from '../../../components/orders/ClientSampleActionModal';
import { formatDate } from '../../../lib/utils';

type OrderItem = NonNullable<GetClientOrderQuery['getClientOrder']>['items'][number];
type Sample = NonNullable<OrderItem['samples']>[number];

/** The live round — the last one. Earlier rounds are history. */
export function liveSample(item: OrderItem): Sample | null {
	const rounds = item.samples ?? [];
	return rounds.length > 0 ? rounds[rounds.length - 1] : null;
}

/**
 * When each sub-step happened. Matched by step id, never by position: the
 * backend words DELIVERY differently for a physical and a virtual sample, and
 * the date behind it is a different field too.
 */
function stepDate(sample: Sample, step: SampleStepEnum): string | null {
	switch (step) {
		case SampleStepEnum.Payment:
			return sample.paidAt ?? null;
		case SampleStepEnum.Production:
			return sample.productionStartedAt ?? null;
		case SampleStepEnum.Ready:
			return sample.productionCompletedAt ?? null;
		case SampleStepEnum.Delivery:
			return sample.shippedAt ?? sample.virtualDeliverySubmittedAt ?? null;
		case SampleStepEnum.Approved:
			return sample.approvedAt ?? null;
		default:
			return null;
	}
}

const roundState = (sample: Sample): SampleRound['state'] =>
	sample.approvedAt ? 'approved' : sample.rejectedAt ? 'declined' : 'current';

/**
 * A product's sampling, from the client's side.
 *
 * ── The same spine, a different turn ──
 *
 * `SampleRounds` is the admin's component too, fed from the same lifecycle, so
 * the two sides cannot show different pictures of how far a round has got. What
 * differs is whose move it is, and the design draws that difference rather than
 * hiding it: while the studio is making the sample the client sees the step it
 * is on marked "In progress" and no buttons, because there is nothing for them
 * to do and a greyed-out control would only suggest otherwise.
 *
 * ── Why the client's move is in a box and the studio's is not ──
 *
 * The admin's moves sit bare on their step: each one advances that step, and
 * the row it is on is its own context. Approving is not that. It is a judgement
 * about the whole round — the photos, the shipment, the thing in the client's
 * hands — and it needs a sentence saying what happens next, because declining
 * starts another round and costs another lead time. That sentence and the two
 * buttons are what `ActionBox` is for.
 *
 * ── Rounds stay readable ──
 *
 * A declined round keeps its files and the reason the client gave. Judging
 * round 2 means comparing it against round 1, and a client who has forgotten
 * what they asked for is being asked to approve blind.
 */
export function ClientSampleRounds({
	item,
	onChanged,
	previewReason,
}: {
	item: OrderItem;
	onChanged: () => void;
	/**
	 * Set while an admin previews the client view.
	 *
	 * Approving and declining are mutations, and a preview session is allowed
	 * exactly four of them — none of these. So the buttons go and the reason
	 * takes their place: a refused approve fails on click with nothing saying
	 * why, which is worse than not offering it.
	 */
	previewReason?: string | null;
}) {
	const [action, setAction] = useState<{ sample: Sample; action: ClientSampleAction } | null>(
		null
	);
	const [preview, setPreview] = useState<{ files: FilePreviewItem[]; index: number } | null>(
		null
	);

	const samples = item.samples ?? [];
	if (samples.length === 0) return null;

	/**
	 * A row of file tiles, opening the app's own preview. One helper, because the
	 * round has two lists now and they have to look the same — what separates them
	 * is where they sit, not how they are drawn.
	 */
	const strip = (files: FilePreviewItem[]) =>
		files.length > 0 ? (
			<div className="mt-2.5 ml-[22px] flex flex-wrap gap-2">
				{files.map((file, i) => (
					<button
						key={file.id}
						type="button"
						onClick={() => setPreview({ files, index: i })}
						className="flex items-center gap-1.5 rounded-md bg-surface-fill px-2.5 py-1.5 text-xs text-text-2 transition-colors hover:text-text-1"
					>
						{file.mimeType.toLowerCase().startsWith('image/') ? (
							<img src={file.url} alt="" className="h-6 w-6 rounded object-cover" />
						) : file.mimeType.toLowerCase().startsWith('video/') ? (
							<Video size={12} className="text-text-4" />
						) : (
							<FileText size={12} className="text-text-4" />
						)}
						{file.name}
					</button>
				))}
			</div>
		) : undefined;

	const rounds = samples.map<SampleRound>((sample) => {
		// The client's own moves on the ROUND — approve, decline.
		//
		// `status != null` is what keeps paying out. A round now carries PAY_SAMPLE
		// as a client MOVE, and without this it landed in the review box: the
		// approve/decline strip appeared on a sample nobody had made yet, captioned
		// "check the files above", with a second "Pay for this sample" button
		// beside the one the payment row already offers.
		//
		// Same distinction the item makes with PAY_PRODUCT: a move that carries a
		// status changes the thing, a move without one moves money. Paying is the
		// payment row's job and it has the amount to state; this box is for the
		// judgement.
		const mine = (sample.availableActions ?? []).filter(
			(a) =>
				a.actor === SampleActionActor.Client &&
				!a.blocked &&
				a.kind === ActionKind.Move &&
				a.status != null
		);
		const virtual = sample.sampleChoice !== OrderSampleChoice.SampleWithShipping;

		// The terminal step is drawn only once it is TRUE.
		//
		// While a round is running, "Approved" as a sub-step would be a dot
		// claiming to be the round's position at the moment it has none, sitting
		// directly under a step already reading "Awaiting review". So it is hidden
		// — and it used to be hidden always, which is how an APPROVED round ended
		// on "Awaiting review" wearing a tick: the stepper said the round was
		// waiting for something that had already happened, and the step that would
		// have said so was the one being filtered out.
		//
		// Once `approvedAt` is set it is the answer, not a claim about the future.
		const steps = (sample.lifecycle ?? [])
			.filter((s) => s.step !== SampleStepEnum.Approved || !!sample.approvedAt)
			.map<SampleStep>((step) => {
				const active = step.current;
				const at = stepDate(sample, step.step);
				return {
					key: step.step,
					label: step.label,
					state: step.completed
						? 'done'
						: active
							? 'active'
							: step.outstanding
								? 'outstanding'
								: 'upcoming',
					at: step.completed && at ? formatDate(at) : undefined,
					// Only while the wait is the studio's — the step says so itself.
					// This used to ask whether the studio had any unblocked action,
					// which SET_PAYMENT_TREATMENT satisfies permanently, so the badge
					// showed on every round forever. Once the round reaches the
					// client's review the box below is the answer, and a badge saying
					// "in progress" beside it would contradict it.
					// What is still owed on a step the round moved past, in the
					// server's words.
					note: step.outstandingReason ?? undefined,
					action:
						active && step.actor === SampleActionActor.Admin && mine.length === 0 ? (
							<Badge tone="info" dot>
								In progress
							</Badge>
						) : undefined,
				};
			});

		/**
		 * Both lists, kept APART.
		 *
		 * They were merged here, which kept a declined round's evidence on the page
		 * — the point — but filed the client's own photographs of what was wrong in
		 * the same strip as the studio's photographs of the sample. The two answer
		 * opposite questions, and the package now has a slot for each.
		 */
		const toItems = (
			list: { id: string; url: string; name: string; mimeType: string; size: number }[]
		) =>
			list
				.filter((f) => f.url)
				.map<FilePreviewItem>((f) => ({
					id: f.id,
					url: f.url,
					name: f.name,
					mimeType: f.mimeType,
					size: f.size,
				}));

		const files = toItems(sample.virtualDeliveryAttachments ?? []);
		const declineFiles = toItems(sample.rejectionAttachments ?? []);

		const tracking = [sample.carrier, sample.trackingNumber].filter(Boolean).join(' ');

		return {
			id: sample.id,
			round: sample.sampleRound,
			state: roundState(sample),
			steps,
			media: strip(files),
			note: sample.virtualDeliveryNotes || undefined,
			declinedAt: sample.rejectedAt ? formatDate(sample.rejectedAt) : undefined,
			declineReason: sample.rejectionReason,
			declineMedia: strip(declineFiles),
			// The `action` slot the package reserved for exactly this and nothing
			// ever filled. `trackingUrl` is resolved server-side from the shared
			// carrier list and is null when the carrier is unrecognised or has no
			// tracking page — so the button appears only when there is somewhere
			// real to go, and the number stays readable on its own when there is
			// not. Never built from the carrier name here: a guessed link that
			// 404s is worse than no link.
			tracking: tracking
				? {
						label: <span className="font-mono text-text-1">{tracking}</span>,
						action: sample.trackingUrl ? (
							<Button
								size="xs"
								variant="outline"
								onClick={() =>
									window.open(sample.trackingUrl!, '_blank', 'noopener')
								}
							>
								Track
								<ExternalLink size={12} data-icon="inline-end" />
							</Button>
						) : undefined,
					}
				: undefined,
			footer: sample.rejectedAt ? (
				/**
				 * What happens next, after a decline.
				 *
				 * The footer only ever rendered while the client had a move, which is
				 * right for the review box and wrong here: declining IS the move, and
				 * the moment it lands the row went silent — no state, no next step,
				 * the client left to wonder whether it registered. The record block
				 * above says what they said; this says what it means.
				 *
				 * Ours to word, like "Waiting on the client's review" on the studio's
				 * side. There is no server sentence for it: a declined round has no
				 * current step, so nothing carries an `actor` to speak for it.
				 */
				<ActionBox>
					<ActionContext>
						The studio has your feedback and will start a new sample round.
					</ActionContext>
				</ActionBox>
			) : mine.length > 0 ? (
				<ActionBox mine>
					<ActionContext>
						<strong className="font-medium text-text-1">Your review:</strong>{' '}
						{virtual ? 'check the files above — ' : ''}approve to move this item on.
						Declining starts a new sample round.
					</ActionContext>
					{previewReason ? (
						<span className="text-2xs text-status-warn">{previewReason}</span>
					) : (
						mine.map((a) => {
							const declining = a.status === OrderSampleStatus.Rejected;
							return (
								<Button
									key={`${a.action}:${a.status ?? ''}`}
									size="sm"
									variant={declining ? 'outline' : 'default'}
									onClick={() =>
										setAction({ sample, action: a as ClientSampleAction })
									}
								>
									{declining ? (
										<X size={12} data-icon="inline-start" />
									) : (
										<Check size={12} data-icon="inline-start" />
									)}
									{a.label}
								</Button>
							);
						})
					)}
				</ActionBox>
			) : undefined,
		};
	});

	return (
		<>
			<SampleRounds rounds={rounds} />

			{action && (
				<ClientSampleActionModal
					action={action.action}
					sampleId={action.sample.id}
					productName={item.productName || 'this item'}
					onClose={() => setAction(null)}
					onSuccess={() => {
						setAction(null);
						onChanged();
					}}
				/>
			)}

			{preview && (
				<FilePreview
					files={preview.files}
					initialIndex={preview.index}
					onClose={() => setPreview(null)}
				/>
			)}
		</>
	);
}
